import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {Message, Hotel, FinancialData,ValuationHistory,UploadedFile,ComparisonData} from '@types';
import { enhancedApi as api } from '@services';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const Chatbot: React.FC = () => {
  console.log('🔄 Chatbot component rendering/re-rendering');

  const [messages, setMessages] = useState<Message[]>([
    {
      sender: 'ai',
      text: 'Welcome to HotelValuAI! Select a hotel and upload your financial documents to get started. After upload, you\'ll be redirected to review and confirm the extracted data.',
      type: 'upload',
    },
  ]);
  const [input, setInput] = useState<string>('');
  const [loading, setLoading] = useState<{
    upload: boolean;
    chat: boolean;
    comparison: boolean;
  }>({
    upload: false,
    chat: false,
    comparison: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [editableData, setEditableData] = useState<FinancialData | null>(null);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [valuationHistory, setValuationHistory] = useState<ValuationHistory[]>([]);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [aiInsights, setAiInsights] = useState<string>('');
  const [openInsightsModal, setOpenInsightsModal] = useState(false);
  const [hotelsLoading, setHotelsLoading] = useState<boolean>(true);
  const [hotelsError, setHotelsError] = useState<string | null>(null);
  const [hotelFinancialContext, setHotelFinancialContext] = useState<any>(null);
  const [contextLoading, setContextLoading] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    console.log('📜 Messages changed, new count:', messages.length);
    console.log('📜 Last 3 messages:', messages.slice(-3).map(m => ({ sender: m.sender, text: m.text.substring(0, 30) + '...' })));
    console.log('🎨 Rendering messages:', messages.length, messages.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })));
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

useEffect(() => {
  const fetchHotels = async () => {
    setHotelsLoading(true);
    setHotelsError(null);
    try {
      // Try different possible token keys that might be used in your app
      const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('access_token');

      if (!token) {
        // Set a more user-friendly error and don't throw - let the component handle it
        setHotelsError('Your session has expired. Please log in again.');
        setMessages((prev) => [
          ...prev,
          {
            sender: 'ai',
            text: 'Session expired. Please log in again to access your hotels.',
            type: 'text',
          },
        ]);
        return; // Exit early instead of throwing
      }

      const response = await axios.get(`${API_BASE_URL}/api/hotels`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Hotel data received:', response.data);
      setHotels(response.data);
    } catch (error: unknown) {
      console.error('Error fetching hotels:', error);
      const errorMsg = error instanceof Error
        ? error.message
        : (error as any)?.response?.data?.detail || (error as any)?.response?.data?.error || 'Unknown error';
      setHotelsError(`Failed to load hotels: ${errorMsg}`);
    } finally {
      setHotelsLoading(false);
    }
  };

  fetchHotels();
}, []);

useEffect(() => {
  const fetchValuationHistory = async () => {
    if (!selectedHotel.length) return;

    try {
      // Get the auth token just like in fetchHotels
      const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('access_token');

      if (!token) {
        console.warn('Token not available for valuation history fetch');
        return;
      }

      // Update the endpoint path - add /api prefix
      const response = await axios.get(`${API_BASE_URL}/api/hotels/${selectedHotel[0]}/valuations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setValuationHistory(response.data);
    } catch (error) {
      console.error('Error fetching valuation history:', error);
      // Don't show this error to the user, just log it
    }
  };

  fetchValuationHistory();
}, [selectedHotel]);

// New useEffect to fetch comprehensive hotel data when hotel is selected
useEffect(() => {
  const fetchHotelFinancialContext = async () => {
    if (!selectedHotel.length) {
      setHotelFinancialContext(null);
      return;
    }

    setContextLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('access_token');

      if (!token) {
        console.warn('Token not available for financial context fetch');
        return;
      }

      // Fetch comprehensive financial data for the selected hotel
      const response = await api.getComprehensiveHotelData(selectedHotel[0]);
      setHotelFinancialContext(response);

      console.log('Hotel financial context loaded:', {
        totalRecords: response.summary.totalFinancialRecords,
        totalDocuments: response.summary.totalDocuments,
        yearsOfData: response.summary.yearsOfData.length,
        availableMetrics: response.summary.availableMetrics.length
      });

      // Add a context message to inform the user
      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          text: `📊 I now have access to comprehensive financial data for ${response.hotel.name}:

• ${response.summary.totalFinancialRecords} financial records
• ${response.summary.totalDocuments} uploaded documents
• ${response.summary.yearsOfData.length} years of data (${response.summary.yearsOfData.join(', ')})
• ${response.summary.availableMetrics.length} different metrics available

I can now provide detailed analysis of your hotel's financial performance, trends, and benchmarks. What would you like to know?`,
          type: 'text',
        },
      ]);

    } catch (error) {
      console.error('Error fetching hotel financial context:', error);
      // Don't show error to user, just log it
    } finally {
      setContextLoading(false);
    }
  };

  fetchHotelFinancialContext();
}, [selectedHotel]);

// Enhanced processUserInput function with financial context
const processUserInput = async (userInput: string) => {
  setLoading((prev) => ({ ...prev, chat: true }));

  try {
    // Get the token for this request too
    const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('access_token');

    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    // Prepare enhanced request with financial context
    const requestPayload = {
      message: userInput,
      hotelId: selectedHotel[0],
      // Include financial context summary for better AI responses
      financialContext: hotelFinancialContext ? {
        hotelName: hotelFinancialContext.hotel.name,
        totalRecords: hotelFinancialContext.summary.totalFinancialRecords,
        yearsOfData: hotelFinancialContext.summary.yearsOfData,
        availableMetrics: hotelFinancialContext.summary.availableMetrics,
        lastUpdated: hotelFinancialContext.summary.lastUpdated,
        // Include recent financial highlights
        recentMetrics: hotelFinancialContext.financialData.hotelMetrics
          .filter((metric: any) => hotelFinancialContext.summary.yearsOfData.includes(metric.year))
          .slice(0, 10), // Last 10 metrics
        recentFinancials: hotelFinancialContext.financialData.confirmedFinancials
          .filter((item: any) => hotelFinancialContext.summary.yearsOfData.includes(item.year))
          .slice(0, 10) // Last 10 financial items
      } : null
    };

    console.log('🚀 Sending chat request:', {
      url: `${API_BASE_URL}/api/chat`,
      payload: requestPayload,
      hasToken: !!token
    });

    // Update the endpoint path - add /api prefix
    const response = await axios.post(
      `${API_BASE_URL}/api/chat`,
      requestPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 30000, // 30 second timeout
      },
    );

    console.log('✅ Chat response received:', response.data);

    // Handle the response based on the backend structure
    let responseText = '';
    if (response.data.success && response.data.data?.response) {
      responseText = response.data.data.response;
      console.log('📝 Using response from data.data.response');
    } else if (response.data.response) {
      responseText = response.data.response;
      console.log('📝 Using response from data.response');
    } else {
      responseText = 'No response received from AI';
      console.warn('⚠️ No valid response found in:', response.data);
    }

    console.log('🤖 Adding AI response to messages:', responseText.substring(0, 100) + '...');
    const aiMessage: Message = {
      sender: 'ai',
      text: responseText,
      type: 'text',
    };

    setMessages((prev) => {
      const newMessages = [...prev, aiMessage];
      console.log('📝 AI response added, total messages:', newMessages.length);
      console.log('📝 Last message:', newMessages[newMessages.length - 1]);
      return newMessages;
    });
  } catch (error: any) {
    console.error('❌ Chat error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    });
    handleError(error, 'process chat message');
  } finally {
    setLoading((prev) => ({ ...prev, chat: false }));
  }
};

  const handleError = (error: any, context: string) => {
    console.error(`Error in ${context}:`, error);
    const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message || `Failed to ${context}`;
    setError(errorMessage);
    setMessages((prev) => [
      ...prev,
      {
        sender: 'ai',
        text: `❌ Error: ${errorMessage}`,
        type: 'text',
      },
    ]);
  };

  const handleMessage = async (message: Message) => {
    console.log('🎯 handleMessage called with:', message);
    setMessages((prev) => {
      const newMessages = [...prev, message];
      console.log('📝 Messages updated, total count:', newMessages.length);
      return newMessages;
    });
    setInput('');

    if (message.type === 'upload') {
      fileInputRef.current?.click();
    } else {
      console.log('🚀 Calling processUserInput with:', message.text);
      await processUserInput(message.text);
    }
  };



  const fetchComparisonData = async () => {
    if (selectedHotel.length < 2) {
      setError('Please select at least two hotels for comparison.');
      return;
    }

    setLoading((prev) => ({ ...prev, comparison: true }));
    try {
      const response = await axios.post(`${API_BASE_URL}/hotels/compare`, {
        hotelIds: selectedHotel,
      });
      setComparisonData(response.data);
    } catch (error) {
      handleError(error, 'fetch comparison data');
    } finally {
      setLoading((prev) => ({ ...prev, comparison: false }));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !selectedHotel[0]) {
      setError('Please select a hotel and a file to upload.');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const activeHotelId = selectedHotel[0];

    // Reset input and state
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError(null);
    setMessages((prev) => [
      ...prev,
      {
        sender: 'user',
        text: `📂 Processing: ${file.name}`,
        type: 'upload',
      },
    ]);
    setLoading((prev) => ({ ...prev, upload: true }));

    try {
      // Use the proper upload API endpoint that returns file records
      const formData = new FormData();
      formData.append('files', file);
      formData.append('hotelId', activeHotelId);
      formData.append('documentType', 'Financial Document');
      formData.append('document_type', 'Financial Document');
      formData.append('category', 'Financial Document');

      const response = await api.post(`/uploads/hotels/${activeHotelId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minute timeout for large files
      });

      // Get the file record from the response
      const fileRecord = response.data?.files?.[0] || response.data?.file || response.data;

      if (!fileRecord || (!fileRecord.id && !fileRecord.fileId && !fileRecord.file_id)) {
        throw new Error('No file ID returned from server - unable to proceed to review');
      }

      const docId = fileRecord.id || fileRecord.fileId || fileRecord.file_id;

      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          text: '✅ File uploaded successfully! Redirecting you to review the extracted data...',
          type: 'text'
        },
      ]);

      // Redirect to the extraction review page
      setTimeout(() => {
        navigate(`/hotels/${activeHotelId}/financial-review/${docId}`);
      }, 1500); // Small delay to show the success message

    } catch (error) {
      console.error('Upload error:', error);

      // Check if this is a 404 error suggesting the new endpoint doesn't exist
      if ((error as any)?.response?.status === 404) {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'ai',
            text: '⚠️ The upload system is not fully configured. Please use the Document Manager on the hotel details page to upload and review files.',
            type: 'text',
          },
        ]);
      } else {
        handleError(error, 'upload file');
      }
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  const handleEditChange = (category: string, year: string, value: string) => {
    if (!/^\d{4}$/.test(year)) {
      setError('Invalid year format. Please enter a 4-digit year.');
      return;
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      setError('Invalid numeric value. Please enter a valid number.');
      return;
    }

    setEditableData((prevData) => {
      if (!prevData) return null;
      return {
        ...prevData,
        data: {
          ...prevData.data,
          [category]: {
            ...prevData.data[category],
            [year]: numericValue,
          },
        },
      };
    });
  };

  const handleConfirmData = async () => {
    if (!editableData || !uploadedFile?.fileId || !selectedHotel.length) {
      setError('Missing required data. Select a hotel and upload a file first.');
      return;
    }

    setLoading((prev) => ({ ...prev, upload: true }));

    try {
      const requestData = {
        fileId: uploadedFile.fileId,
        editedData: editableData.data,
        hotelId: selectedHotel[0],
      };

      const response = await axios.post(`${API_BASE_URL}/ocr/confirm`, requestData);

      if (!response.data?.insights) {
        throw new Error('No insights received from AI.');
      }

      // Parse insights if it's a string, otherwise use as is
      const parsedInsights = typeof response.data.insights === 'string' ? JSON.parse(response.data.insights) : response.data.insights;

      // Format insights for display
      const formattedInsights = `
Summary: ${parsedInsights.summary}

Key Metrics:
- Revenue Growth: ${parsedInsights.keyMetrics.revenueGrowth}
- Profit Margin: ${parsedInsights.keyMetrics.profitMargin}
- Occupancy Rate: ${parsedInsights.keyMetrics.occupancyRate}
- ADR: ${parsedInsights.keyMetrics.adr}
- RevPAR: ${parsedInsights.keyMetrics.revpar}

Risk Level: ${parsedInsights.riskLevel}

Recommendations:
${parsedInsights.recommendations.map((rec: string) => `- ${rec}`).join('\n')}

Valuation Range:
- Low: ${parsedInsights.valuationRange.low.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
- High: ${parsedInsights.valuationRange.high.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
    `.trim();

      setAiInsights(formattedInsights);
      setOpenInsightsModal(true);

      setMessages((prev) => [
        ...prev,
        { sender: 'ai', text: '✅ Data saved successfully! Here are the AI-generated valuation insights:', type: 'text' },
        { sender: 'ai', text: parsedInsights.summary, type: 'text' },
      ]);

      // Clear state after successful confirmation
      setEditableData(null);
      setUploadedFile(null);
    } catch (error) {
      handleError(error, 'confirm data');
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  // Add a new function to add a category row manually
  const handleAddCategory = () => {
    if (!editableData) return;

    const newCategory = `New Category ${Object.keys(editableData.data).length + 1}`;

    setEditableData((prevData) => {
      if (!prevData) return null;

      const newDataRow: Record<string, number> = {};
      prevData.years.forEach((year) => {
        newDataRow[year] = 0;
      });

      return {
        ...prevData,
        categories: [...prevData.categories, newCategory],
        data: {
          ...prevData.data,
          [newCategory]: newDataRow,
        },
      };
    });
  };

  const handleHotelChange = (event: SelectChangeEvent<string[]>) => {
    const newSelectedHotels = event.target.value as string[];
    setSelectedHotel(newSelectedHotels);

    // Clear any previous errors
    setError(null);

    // Reset comparison data if hotel selection changes
    if (comparisonData) {
      setComparisonData(null);
    }

    // If user deselects all hotels, provide gentle feedback
    if (newSelectedHotels.length === 0) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          text: 'Please select at least one hotel to proceed with analysis or file upload.',
          type: 'text',
        },
      ]);
    }

    console.log(
      'Selected hotels:',
      newSelectedHotels.map((id) => hotels.find((h) => h.id === id)?.name || id),
    );
  };

  return (
    <Box sx={{ maxWidth: '95%', margin: 'auto', padding: 3 }} className="fade-in">
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}></Typography>
      {error && (
        <Fade in={!!error}>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} variant="filled">
            {error}
          </Alert>
        </Fade>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Hotels to Compare</InputLabel>
        <Select
          multiple
          value={selectedHotel}
          onChange={handleHotelChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((id) => (
                <Chip key={id} label={hotels.find((h) => h.id === id)?.name || id} color="primary" size="small" />
              ))}
            </Box>
          )}
          disabled={loading.upload || hotelsLoading}
          endAdornment={hotelsLoading && <CircularProgress size={20} sx={{ marginRight: 2 }} />}
        >
          {hotelsLoading ? (
            <MenuItem key="loading" disabled>
              Loading hotels...
            </MenuItem>
          ) : hotelsError ? (
            <MenuItem key="error" disabled>
              Error loading hotels
            </MenuItem>
          ) : hotels.length === 0 ? (
            <MenuItem key="no-hotels" disabled>
              No hotels available
            </MenuItem>
          ) : (
            hotels.map((hotel) => (
              <MenuItem key={hotel.id} value={hotel.id}>
                {hotel.name}
              </MenuItem>
            ))
          )}
        </Select>
        {hotelsError && (
          <Typography color="error" variant="caption" sx={{ mt: 1 }}>
            {hotelsError}
          </Typography>
        )}
      </FormControl>

      {/* Debug info */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Debug: {messages.length} messages | Loading: {loading.chat ? 'Yes' : 'No'}
      </Typography>

      {/* Chat Messages and Financial Data Table */}
      <Paper
        elevation={2}
        sx={{
          padding: 3,
          height: '60vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          position: 'relative',
          borderRadius: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'background.paper',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'primary.light',
            borderRadius: '4px',
          },
        }}
      >
        {loading.upload && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {messages.map((msg, index) => (
          <Box
            key={`message-${index}-${msg.sender}-${msg.text.substring(0, 10)}`}
            sx={{
              textAlign: msg.sender === 'user' ? 'right' : 'left',
              mb: 2,
              opacity: loading.chat && index === messages.length - 1 ? 0.5 : 1,
              border: '1px solid red', // Debug border
            }}
          >
            <Paper
              elevation={1}
              sx={{
                display: 'inline-block',
                maxWidth: '80%',
                p: 2,
                bgcolor: msg.sender === 'user' ? 'primary.light' : 'background.default',
                color: msg.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                borderRadius: msg.sender === 'user' ? '20px 20px 0 20px' : '20px 20px 20px 0',
              }}
            >
              <Typography>
                [{index}] {msg.sender}: {msg.text}
              </Typography>
            </Paper>
          </Box>
        ))}

        {comparisonData && (
          <Paper elevation={2} sx={{ mt: 3, p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Hotel Comparison
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  {Object.keys(comparisonData).map((hotelId) => (
                    <TableCell key={hotelId} align="right">
                      {hotels.find((h) => h.id === hotelId)?.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(comparisonData[selectedHotel[0]] || {}).map(([category, values]) => (
                  <TableRow key={category} hover>
                    <TableCell>{category}</TableCell>
                    {Object.keys(comparisonData).map((hotelId) => (
                      <TableCell key={hotelId} align="right">
                        {Object.values(comparisonData[hotelId][category] || {})
                          .map((v) =>
                            v.toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                            }),
                          )
                          .join(', ')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* Invisible div to scroll to */}
        <div ref={messagesEndRef} />
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading.chat}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleMessage({ sender: 'user', text: input, type: 'text' });
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '30px',
              pr: 1,
            },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => handleMessage({ sender: 'user', text: input, type: 'text' })}
          disabled={!input.trim() || loading.chat}
          sx={{ borderRadius: '30px', minWidth: '120px' }}
        >
          {loading.chat ? <CircularProgress size={24} /> : <SendIcon />}
        </Button>

        <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv" />

        <Button variant="outlined" color="primary" onClick={() => fileInputRef.current?.click()} disabled={!selectedHotel.length || loading.upload} sx={{ borderRadius: '30px', minWidth: '120px' }}>
          <AttachFileIcon />
        </Button>

        {selectedHotel.length >= 2 && (
          <Button variant="outlined" color="info" onClick={fetchComparisonData} disabled={loading.comparison} sx={{ borderRadius: '30px', minWidth: '120px' }}>
            {loading.comparison ? <CircularProgress size={24} /> : <CompareArrowsIcon />}
          </Button>
        )}

                {/* Debug button to test message rendering */}
        <Button
          variant="outlined"
          color="warning"
          onClick={() => {
            console.log('🧪 Adding test message');
            console.log('🧪 Current messages before:', messages.length);
            setMessages(prev => {
              const testMessage: Message = {
                sender: 'ai',
                text: `Test AI message at ${new Date().toLocaleTimeString()}`,
                type: 'text'
              };
              const newMessages = [...prev, testMessage];
              console.log('🧪 New messages after:', newMessages.length);
              return newMessages;
            });
          }}
          sx={{ borderRadius: '30px', minWidth: '120px' }}
        >
          Test AI
        </Button>
      </Box>

      <Dialog
        open={openInsightsModal}
        onClose={() => setOpenInsightsModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">AI Valuation Insights</Typography>
          <IconButton onClick={() => setOpenInsightsModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontSize: '1rem',
              p: 2,
              backgroundColor: 'background.default',
              borderRadius: 1,
            }}
          >
            {aiInsights}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Chatbot;